import React, {Component} from 'react';
import PropTypes from 'prop-types';

import alert from '@drivesally/sally-ui/lib/libs/alert';
import parseError from '@drivesally/sally-fw/lib/error/parseError';
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';

import createTagApi from '../../api/createTag.api.crm';
import deleteTagApi from '../../api/deleteTag.api.crm';
import listTagsApi from '../../api/listTags.api.crm';
import updateTagApi from '../../api/updateTag.api.crm';

import CrmTagsModal from '../../components/CrmTagsModal/CrmTagsModal';

import showTagsModalEvent from '../../events/showTagsModal.event.crm';
import tagChangedEvent from '../../events/tagChanged.event.crm';

const DEFAULT_TAG_COLOR = '#6B7280';

const sortTags = (tags) =>
  [...tags].sort((a, b) => a.tag.localeCompare(b.tag));

class CrmTagsContainer extends Component {
  static propTypes = {
    subscribe: PropTypes.func,
  };

  state = {
    creating: false,
    deletingId: null,
    loading: false,
    newTag: '',
    newTagColor: DEFAULT_TAG_COLOR,
    tagEdits: {},
    tags: [],
    updatingId: null,
    visible: false,
  };

  componentDidMount() {
    this.mounted = true;
    this.props.subscribe(showTagsModalEvent.subscribe(this.show));
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  show = () => {
    this.setState(
      {
        visible: true,
        newTag: '',
        newTagColor: DEFAULT_TAG_COLOR,
        tagEdits: {},
      },
      this.loadTags
    );
  };

  hide = () => {
    const {creating, deletingId, loading, updatingId} = this.state;
    if (creating || deletingId || loading || updatingId) return;
    this.setState({visible: false, tagEdits: {}});
  };

  loadTags = async () => {
    this.setState({loading: true});
    try {
      const {results} = await listTagsApi({limit: 1000, ordering: 'tag'});
      if (!this.mounted) return;
      this.setState({tags: results, loading: false, tagEdits: {}});
    } catch (error) {
      if (!this.mounted) return;
      alert.info(`Failed to fetch CRM tags: ${error}`);
      this.setState({loading: false, tags: [], tagEdits: {}});
    }
  };

  onNewTagChange = (newTag) => {
    this.setState({newTag});
  };

  onNewTagColorChange = (event) => {
    this.setState({newTagColor: event.target.value});
  };

  onTagColorChange = (tag) => (event) => {
    this.updateTag(tag, {color: event.target.value});
  };

  onTagNameChange = (tag) => (value) => {
    this.setState({
      tagEdits: {...this.state.tagEdits, [tag.id]: value},
    });
  };

  onTagNameBlur = (tag) => () => {
    const {tagEdits} = this.state;
    const editedName =
      tagEdits[tag.id] !== undefined ? tagEdits[tag.id] : tag.tag;
    const name = editedName.trim();

    if (!name.length || name === tag.tag) {
      this.setState({
        tagEdits: {...tagEdits, [tag.id]: tag.tag},
      });
      return;
    }

    this.updateTag(tag, {tag: name});
  };

  updateTag = async (tag, payload) => {
    const {creating, deletingId, updatingId} = this.state;
    if (creating || deletingId || updatingId) return;

    const hasChange = Object.entries(payload).some(
      ([key, value]) => tag[key] !== value
    );
    if (!hasChange) return;

    this.setState({updatingId: tag.id});
    try {
      const response = await updateTagApi(tag.id, payload);
      if (!this.mounted) return;
      const {tagEdits} = this.state;
      const {[tag.id]: removedEdit, ...remainingEdits} = tagEdits;
      this.setState({
        updatingId: null,
        tagEdits: remainingEdits,
        tags: sortTags(
          this.state.tags.map((existingTag) =>
            existingTag.id === response.id ? response : existingTag
          )
        ),
      });
      tagChangedEvent.publish(response);
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      if (!this.mounted) return;
      this.setState({
        updatingId: null,
        tagEdits: {...this.state.tagEdits, [tag.id]: tag.tag},
      });
    }
  };

  create = async () => {
    const {creating, newTag, newTagColor} = this.state;
    const tag = newTag.trim();
    if (creating || !tag.length) return;

    this.setState({creating: true});
    try {
      const response = await createTagApi({tag, color: newTagColor});
      if (!this.mounted) return;
      this.setState({
        creating: false,
        newTag: '',
        newTagColor: DEFAULT_TAG_COLOR,
        tags: sortTags([...this.state.tags, response]),
      });
      tagChangedEvent.publish(response);
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      if (!this.mounted) return;
      this.setState({creating: false});
    }
  };

  remove = (tag) => async () => {
    const {creating, deletingId, updatingId} = this.state;
    if (creating || deletingId || updatingId) return;

    this.setState({deletingId: tag.id});
    try {
      await deleteTagApi(tag.id);
      if (!this.mounted) return;
      const {tagEdits} = this.state;
      const {[tag.id]: removedEdit, ...remainingEdits} = tagEdits;
      this.setState({
        deletingId: null,
        tagEdits: remainingEdits,
        tags: this.state.tags.filter(({id}) => id !== tag.id),
      });
      tagChangedEvent.publish(tag);
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      if (!this.mounted) return;
      this.setState({deletingId: null});
    }
  };

  render() {
    const {
      creating,
      deletingId,
      loading,
      newTag,
      newTagColor,
      tagEdits,
      tags,
      updatingId,
      visible,
    } = this.state;

    return (
      <CrmTagsModal
        creating={creating}
        deletingId={deletingId}
        loading={loading}
        newTag={newTag}
        newTagColor={newTagColor}
        onClose={this.hide}
        onCreate={this.create}
        onDelete={this.remove}
        onNewTagChange={this.onNewTagChange}
        onNewTagColorChange={this.onNewTagColorChange}
        onTagColorChange={this.onTagColorChange}
        onTagNameBlur={this.onTagNameBlur}
        onTagNameChange={this.onTagNameChange}
        tagEdits={tagEdits}
        tags={tags}
        updatingId={updatingId}
        visible={visible}
      />
    );
  }
}

export default subscriptionHOC(CrmTagsContainer);
